import { useState } from 'react';

import { ArtistSelect } from '@/components/artist-select';
import { LibraryPicker } from '@/components/library-picker';
import { ProjectSelect } from '@/components/project-select';
import { RichTextEditor } from '@/components/rich-text-editor';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { MediaRef } from '@/lib/media';

export interface ProposalValues {
  title: string;
  projectId: string;
  project: string;
  artistId: string;
  artist: string;
  status: string;
  description: string;
  coverMedia: MediaRef | null;
  gallery: MediaRef[];
}

const statuses = ['In review', 'Shortlisted', 'Winner', 'Not selected'] as const;
const DEFAULT_STATUS = 'In review';
const STEPS = ['Details', 'Description', 'Media'] as const;

interface ProposalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  initialValues?: Partial<ProposalValues>;
  onSubmit: (values: ProposalValues) => void;
}

export function ProposalDialog({
  open,
  onOpenChange,
  mode,
  initialValues,
  onSubmit,
}: ProposalDialogProps) {
  const [step, setStep] = useState(0);
  const [title, setTitle] = useState(initialValues?.title ?? '');
  const [projectId, setProjectId] = useState(initialValues?.projectId ?? '');
  const [project, setProject] = useState(initialValues?.project ?? '');
  const [artistId, setArtistId] = useState(initialValues?.artistId ?? '');
  const [artist, setArtist] = useState(initialValues?.artist ?? '');
  const [status, setStatus] = useState(initialValues?.status ?? DEFAULT_STATUS);
  const [description, setDescription] = useState(initialValues?.description ?? '');
  const [coverMedia, setCoverMedia] = useState<MediaRef | null>(initialValues?.coverMedia ?? null);
  const [gallery, setGallery] = useState<MediaRef[]>(initialValues?.gallery ?? []);
  const [showErrors, setShowErrors] = useState(false);

  const isEdit = mode === 'edit';
  const isLast = step === STEPS.length - 1;
  const detailsValid = Boolean(title.trim() && projectId && artistId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="grid gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (!isLast) {
              if (step === 0 && !detailsValid) {
                setShowErrors(true);
                return;
              }
              setStep((value) => value + 1);
              return;
            }
            if (!detailsValid) {
              setShowErrors(true);
              setStep(0);
              return;
            }
            onSubmit({
              title,
              projectId,
              project,
              artistId,
              artist,
              status,
              description,
              coverMedia,
              gallery,
            });
            onOpenChange(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit proposal' : 'New proposal'}</DialogTitle>
            <DialogDescription>
              Step {step + 1} of {STEPS.length} · {STEPS[step]}
            </DialogDescription>
          </DialogHeader>

          {step === 0 && (
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="proposal-title">Title</Label>
                <Input
                  id="proposal-title"
                  name="title"
                  placeholder="e.g. Still Water"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label>Project</Label>
                <ProjectSelect
                  value={project}
                  onChange={(option) => {
                    setProjectId(option.id);
                    setProject(option.name);
                  }}
                />
                {showErrors && !projectId && (
                  <p className="text-destructive text-xs">Select a project.</p>
                )}
              </div>

              <div className="grid gap-2">
                <Label>Artist</Label>
                <ArtistSelect
                  value={artist}
                  onChange={(option) => {
                    setArtistId(option.id);
                    setArtist(option.name);
                  }}
                />
                {showErrors && !artistId && (
                  <p className="text-destructive text-xs">Select an artist.</p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="proposal-status">Status</Label>
                <Select value={status} onValueChange={setStatus} name="status">
                  <SelectTrigger id="proposal-status" className="w-full">
                    <SelectValue placeholder="Select a status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="grid gap-2">
              <Label>Description</Label>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="Short summary of the proposal"
              />
            </div>
          )}

          {step === 2 && (
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label>Cover image</Label>
                <LibraryPicker value={coverMedia} onChange={setCoverMedia} types={['image']} />
              </div>

              <div className="grid gap-2">
                <Label>Gallery</Label>
                <LibraryPicker multiple values={gallery} onChange={setGallery} types={['image']} />
              </div>
            </div>
          )}

          <DialogFooter className="justify-between pt-2 sm:justify-between">
            <div>
              {step > 0 && (
                <Button type="button" variant="ghost" onClick={() => setStep((value) => value - 1)}>
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {step === 0 && (
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
              )}
              <Button type="submit">
                {!isLast ? 'Next' : isEdit ? 'Save changes' : 'Create proposal'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
